import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import CLUB from '../../config/club.js'
import { HUSTLER } from '../../config/siteMode.js'
import { listCourses, getCourse } from './ggCourses.js'
import { calcCourseHandicapRaw, calcPlayingHandicap, strokesOnHole, DEFAULT_STROKE_INDEX, ALLOWANCE_OPTIONS } from './ggHandicap.js'
import { loadMemberGame, getMemberId } from '../../lib/memberGgc.js'
import { createMasterRound, expandPlayer } from '../../lib/masterRounds.js'
import { QRCodeSVG } from 'qrcode.react'
import { ROUND_GAMES, anyTeamGame } from './ggRoundSettle.js'
import { lookupGhinHandicap } from '../../lib/ghinLookup.js'

// Handicap & Multi-Group — the members' website version of the app's Group Round.
// One organizer sets up the course + game + bet and everyone's handicap once, splits
// the field into groups, and gets a shareable round. Course/Playing Handicaps + per-
// hole strokes are computed with the same handicap engine. Persisted to the member's
// account (a 'multigroup' /ggGames record); the share link reopens a read-only view
// so every group sees the same setup + their strokes. (Live cross-group scoring on the
// course is played in the companion app.)
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
// Golf Hustler dark theme: green labels/pills would be invisible on black, so use
// light label text + dark/lime pills. Styling only — same toggles and behavior.
const LABEL_COLOR = HUSTLER ? '#e7e7e7' : PRIMARY
const pillStyle = (on) => HUSTLER
  ? (on ? { background: '#9CC400', color: '#0a0a0a', border: '1px solid transparent' } : { background: '#1a1a1a', color: '#e7e7e7', border: '1px solid #3a3a3a' })
  : (on ? { background: PRIMARY, color: '#fff', border: '1px solid transparent' } : { background: '#fff', color: PRIMARY, border: '1px solid #D8DED8' })
const GAMES = ['wolf', 'skins', 'stroke', 'match', 'scramble']

function normCourse(c) {
  const par = Number(c.par) || 72
  return {
    id: String(c.id), name: c.name, par,
    pars: Array.isArray(c.pars) && c.pars.length === 18 ? c.pars.map(Number) : Array(18).fill(Math.round(par / 18)),
    strokeIndex: Array.isArray(c.strokeIndex) && c.strokeIndex.length === 18 ? c.strokeIndex.map(Number) : DEFAULT_STROKE_INDEX,
    tees: (c.tees || c.teeBoxes || []).map((t) => ({ name: t.name, slope: Number(t.slope) || 113, rating: Number(t.rating) || par })),
  }
}
const blankP = (match = 1, team = 'A') => ({ name: '', ghin: '', ghinStatus: 'idle', idx: '', allowance: 100, match, team })

export default function GMultiGroup() {
  const [params] = useSearchParams()
  const roundId = params.get('round')
  if (roundId) return <GroupRoundView id={roundId} />
  return <Organizer />
}

function Organizer() {
  const courses = useMemo(() => {
    const seen = new Set(); const out = []
    for (const c of listCourses()) { const nc = normCourse(c); if (!seen.has(nc.id)) { seen.add(nc.id); out.push(nc) } }
    if (!out.length) out.push(normCourse(getCourse('pinebluff')))
    return out
  }, [])
  const [course, setCourse] = useState(courses[0])
  const [teeIdx, setTeeIdx] = useState(0)
  const [name, setName] = useState('')
  const [games, setGames] = useState(['stroke'])
  const [useHandicap, setUseHandicap] = useState(true)
  const [hcMode, setHcMode] = useState('cap1')
  const [gameOpts, setGameOpts] = useState({ skins: { amount: 2, carry: true, double: false }, scats: { buyIn: 10 }, stroke: { bet: 0 }, match: { bet: 5 }, team: { bet: 5 } })
  const setOpt = (g, k, v) => setGameOpts((p) => ({ ...p, [g]: { ...p[g], [k]: v } }))
  const toggleGame = (id) => setGames((prev) => {
    if (id === 'practice') return prev.includes('practice') ? ['stroke'] : ['practice']
    const next = prev.filter((x) => x !== 'practice')
    const has = next.includes(id)
    const out = has ? next.filter((x) => x !== id) : [...next, id]
    return out.length ? out : [id]
  })
  const teamGame = anyTeamGame(games)
  const [groupCount, setGroupCount] = useState(2)
  const [noBets, setNoBets] = useState(false) // bets ON by default; ON = no money / excluded from settlement
  const [rows, setRows] = useState([blankP(1, 'A'), blankP(1, 'B'), blankP(2, 'A'), blankP(2, 'B')])
  const [busy, setBusy] = useState(false)
  const [savedId, setSavedId] = useState(null) // opaque round id once created (never typed)

  const tee = course.tees[teeIdx] || course.tees[0] || { name: 'Blue', slope: 113, rating: course.par }
  const named = rows.filter((r) => r.name.trim())

  const calc = (r) => {
    const idx = parseFloat(r.idx)
    if (!Number.isFinite(idx)) return { ch: null, ph: null, strokes: null }
    const chRaw = calcCourseHandicapRaw({ handicapIndex: idx, slopeRating: tee.slope, courseRating: tee.rating, par: course.par })
    const ph = calcPlayingHandicap(chRaw, Number(r.allowance) || 100)
    const strokes = Array.from({ length: 18 }, (_, h) => strokesOnHole(ph, h, course.strokeIndex)).reduce((a, b) => a + b, 0)
    return { ch: Math.round(chRaw), ph, strokes }
  }

  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const addRow = () => setRows((prev) => [...prev, blankP(1, 'A')])
  const delRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i))

  // GH# lookup — same web util the Wolf setup uses; fills the handicap index so
  // CH → PH → Strokes compute.
  const lookupRowGhin = async (i) => {
    const ghin = (rows[i]?.ghin || '').replace(/\D/g, '')
    if (!ghin) return
    setRow(i, { ghinStatus: 'loading' })
    const res = await lookupGhinHandicap(ghin)
    if (res?.ok) setRow(i, { idx: String(res.handicapIndex), ghinStatus: 'ok', name: rows[i]?.name?.trim() ? rows[i].name : (res.fullName || '') })
    else setRow(i, { ghinStatus: 'miss' })
  }

  const create = async () => {
    if (named.length < 2) { alert('Add at least 2 named players.'); return }
    if (!getMemberId()) { alert('Sign in as a member first.'); return }
    setBusy(true)
    try {
      const courseObj = { id: course.id, name: course.name, par: course.par, pars: course.pars, strokeIndex: course.strokeIndex, teeName: tee.name, slope: tee.slope, rating: tee.rating }
      const players = named.map((r, i) => expandPlayer({
        id: `p${i + 1}`, name: r.name.trim(), ghin: r.ghin || null,
        handicapIndex: Number.isFinite(parseFloat(r.idx)) ? parseFloat(r.idx) : null,
        allowance: Number(r.allowance) || 100, teeName: tee.name,
        group: Math.min(Math.max(1, Number(r.match) || 1), Number(groupCount) || 1),
        team: r.team === 'B' ? 'B' : 'A',
      }, courseObj, tee))
      const opts = {}
      if (games.includes('skins')) opts.skins = { amount: Number(gameOpts.skins?.amount) || 0, carry: gameOpts.skins?.carry !== false, double: !!gameOpts.skins?.double, mode: hcMode, useHandicap: true }
      if (games.includes('scats')) opts.scats = { buyIn: Number(gameOpts.scats?.buyIn) || 10, mode: hcMode, useHandicap: true }
      if (games.includes('stroke')) opts.stroke = { bet: Number(gameOpts.stroke?.bet) || 0, mode: hcMode }
      if (games.includes('match')) opts.match = { bet: Number(gameOpts.match?.bet) || 0, mode: hcMode }
      ;['fourball', 'scramble', 'shamble'].forEach((g) => { if (games.includes(g)) opts[g] = { bet: Number(gameOpts.team?.bet) || 0, mode: hcMode } })
      const { id } = await createMasterRound({
        name: name.trim() || `Group Round — ${players.length} players`,
        course: courseObj, games, useHandicap, gameOpts: opts, players, groupCount: Number(groupCount) || 1, noBets,
      })
      setSavedId(id)
    } catch (e) { alert(`Could not create the round:\n${e?.message || e}`) }
    finally { setBusy(false) }
  }

  if (savedId) {
    const scorerUrl = `${location.origin}/admin/gg/mg?r=${savedId}`
    return (
      <div className="card" style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>⛳</div>
        <h3 className="text-xl font-extrabold" style={{ color: PRIMARY }}>Match created</h3>
        <p className="text-gray-600 mt-1">Show this <b>Scorekeeper QR</b> — the other scorekeepers scan it off your screen to claim their match. No code to type.</p>
        <div className="inline-block p-4 mt-3 rounded-xl" style={{ background: '#fff', border: `2px solid ${ACCENT}` }}>
          <QRCodeSVG value={scorerUrl} size={220} />
          <div className="text-xs font-bold mt-2" style={{ color: PRIMARY }}>SCOREKEEPER QR</div>
        </div>
        <div className="flex gap-2 mt-4 justify-center flex-wrap">
          <button className="btn-primary" style={{ backgroundColor: PRIMARY }} onClick={() => navigator.clipboard?.writeText(scorerUrl).then(() => alert('Scorekeeper link copied'))}>Copy scorekeeper link</button>
          <a className="btn-primary" style={{ backgroundColor: ACCENT, color: '#1a2e12' }} href={scorerUrl}>Open my match →</a>
        </div>
        <p className="text-xs text-gray-500 mt-3">You're a scorekeeper too — tap "Open my match" to claim + score yours, then show your <b>Player QR</b> so your players can follow.</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1"><span style={{ fontSize: 22 }}>📐</span><h3 className="font-bold text-xl">Handicap &amp; Multi-Group</h3></div>
      <p className="text-sm text-gray-500 mb-4">Set up a group round — course, game, and everyone's handicap, once. Split the field into groups and share one link.</p>

      <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Round name (optional)
        <input className="border rounded px-3 py-2 text-base" value={name} onChange={(e) => setName(e.target.value)} placeholder="Saturday Group Round" /></label>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Course
          <select className="border rounded px-3 py-2 text-base" value={course.id} onChange={(e) => { const c = courses.find((x) => x.id === e.target.value); if (c) { setCourse(c); setTeeIdx(0) } }}>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.name} · par {c.par}</option>)}
          </select></label>
        <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800">Tee (rating / slope)
          <select className="border rounded px-3 py-2 text-base" value={teeIdx} onChange={(e) => setTeeIdx(Number(e.target.value))}>
            {course.tees.map((t, i) => <option key={i} value={i}>{t.name} · {t.rating} / {t.slope}</option>)}
          </select></label>
        <label className="flex flex-col gap-1 text-lg font-semibold text-gray-800"># of groups
          <input type="number" min={1} max={8} className="border rounded px-3 py-2 text-base w-28" value={groupCount} onChange={(e) => setGroupCount(Math.min(8, Math.max(1, Number(e.target.value) || 1)))} /></label>
        <label className="flex items-center gap-2 text-base font-semibold self-end pb-2" style={{ color: LABEL_COLOR }} title="No money — round is scored/tracked normally but excluded from settle-up">
          <input type="checkbox" className="w-5 h-5" checked={noBets} onChange={(e) => setNoBets(e.target.checked)} /> No bets
        </label>
      </div>

      <div className="mt-5 flex items-center gap-3 mb-2">
        <span className="h-px w-6" style={{ background: ACCENT }} />
        <span className="text-xs font-extrabold uppercase tracking-widest" style={{ color: PRIMARY }}>Games</span>
        <span className="h-px flex-1" style={{ background: `${ACCENT}55` }} />
      </div>
      <p className="text-sm text-gray-500 mb-2">Pick one or more (Practice is on its own). Every group plays the same games.</p>
      <div className="flex flex-wrap gap-2">
        {ROUND_GAMES.map((g) => { const on = games.includes(g.id); return (
          <button key={g.id} type="button" onClick={() => toggleGame(g.id)} className="px-3 py-1.5 rounded-full text-sm font-bold" style={pillStyle(on)}>{g.label}{g.team ? ' 👥' : ''}</button>
        )})}
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3">
        <label className="flex items-center gap-2 text-base font-semibold" style={{ color: LABEL_COLOR }}>
          <input type="checkbox" className="w-5 h-5" checked={useHandicap} onChange={(e) => setUseHandicap(e.target.checked)} /> Use Handicap
        </label>
        {useHandicap ? <label className="text-sm">Mode <select className="border rounded px-2 py-1" value={hcMode} onChange={(e) => setHcMode(e.target.value)}><option value="full">Full</option><option value="cap1">Cap 1</option><option value="lowest">Off lowest</option></select></label> : <span className="text-sm text-gray-500">Gross (no handicap)</span>}
        {games.includes('skins') ? <label className="text-sm">Skins $/skin <input type="number" className="border rounded px-2 py-1 w-16" disabled={noBets} style={noBets ? { backgroundColor: '#EEEEEA', color: '#9AA0A6' } : { backgroundColor: '#E4F0E4' }} value={gameOpts.skins?.amount ?? 2} onChange={(e) => setOpt('skins', 'amount', e.target.value)} /></label> : null}
        {games.includes('scats') ? <label className="text-sm">Scats buy-in $ <input type="number" className="border rounded px-2 py-1 w-16" disabled={noBets} style={noBets ? { backgroundColor: '#EEEEEA', color: '#9AA0A6' } : { backgroundColor: '#E4F0E4' }} value={gameOpts.scats?.buyIn ?? 10} onChange={(e) => setOpt('scats', 'buyIn', e.target.value)} /></label> : null}
        {games.includes('stroke') ? <label className="text-sm">Stroke $ (low net) <input type="number" className="border rounded px-2 py-1 w-16" disabled={noBets} style={noBets ? { backgroundColor: '#EEEEEA', color: '#9AA0A6' } : { backgroundColor: '#E4F0E4' }} value={gameOpts.stroke?.bet ?? 0} onChange={(e) => setOpt('stroke', 'bet', e.target.value)} /></label> : null}
        {games.includes('match') ? <label className="text-sm">Match $/hole <input type="number" className="border rounded px-2 py-1 w-16" disabled={noBets} style={noBets ? { backgroundColor: '#EEEEEA', color: '#9AA0A6' } : { backgroundColor: '#E4F0E4' }} value={gameOpts.match?.bet ?? 5} onChange={(e) => setOpt('match', 'bet', e.target.value)} /></label> : null}
        {teamGame ? <label className="text-sm">Team $/match <input type="number" className="border rounded px-2 py-1 w-16" disabled={noBets} style={noBets ? { backgroundColor: '#EEEEEA', color: '#9AA0A6' } : { backgroundColor: '#E4F0E4' }} value={gameOpts.team?.bet ?? 5} onChange={(e) => setOpt('team', 'bet', e.target.value)} /></label> : null}
      </div>

      <div className="mt-5 flex items-center gap-3 mb-2">
        <span className="h-px w-6" style={{ background: ACCENT }} />
        <span className="text-xs font-extrabold uppercase tracking-widest" style={{ color: PRIMARY }}>Players &amp; handicaps</span>
        <span className="h-px flex-1" style={{ background: `${ACCENT}55` }} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>
            {['Name', 'GH#', 'HC Index', 'Allow %', 'Group', ...(teamGame ? ['Team'] : []), 'CH', 'PH', 'Strokes', ''].map((h, hi) => <th key={hi} style={{ border: '1px solid #eee', padding: '6px 8px', textAlign: 'left' }}>{h}</th>)}
          </tr></thead>
          <tbody>{rows.map((r, i) => {
            const { ch, ph, strokes } = calc(r)
            return (
              <tr key={i}>
                <td style={{ border: '1px solid #eee', padding: 4 }}><input className="border rounded px-2 py-1 w-full min-w-[180px]" value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder={`Player ${i + 1}`} /></td>
                <td style={{ border: '1px solid #eee', padding: 4 }}>
                  <div className="flex items-center gap-1">
                    <input className="border rounded px-2 py-1 w-28" value={r.ghin || ''} onChange={(e) => setRow(i, { ghin: e.target.value.replace(/\D/g, ''), ghinStatus: 'idle' })} placeholder="Not Required" inputMode="numeric" />
                    <button type="button" title="Look up handicap index" className="px-2 py-1 rounded" style={{ background: '#EDF4EE', color: '#1B4D2E', opacity: r.ghinStatus === 'loading' ? 0.6 : 1 }} disabled={r.ghinStatus === 'loading'} onClick={() => lookupRowGhin(i)}>{r.ghinStatus === 'loading' ? '…' : r.ghinStatus === 'ok' ? '✓' : r.ghinStatus === 'miss' ? '✕' : '🔎'}</button>
                  </div>
                </td>
                <td style={{ border: '1px solid #eee', padding: 4 }}><input className="border rounded px-2 py-1 w-20" value={r.idx} onChange={(e) => setRow(i, { idx: e.target.value })} placeholder="12.3" /></td>
                <td style={{ border: '1px solid #eee', padding: 4 }}>
                  <select className="border rounded px-1 py-1" value={r.allowance} onChange={(e) => setRow(i, { allowance: Number(e.target.value) })}>{ALLOWANCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select></td>
                <td style={{ border: '1px solid #eee', padding: 4 }}>
                  <select className="border rounded px-1 py-1" value={r.match} onChange={(e) => setRow(i, { match: Number(e.target.value) })}>{Array.from({ length: groupCount }, (_, g) => g + 1).map((g) => <option key={g} value={g}>{g}</option>)}</select></td>
                {teamGame ? <td style={{ border: '1px solid #eee', padding: 4 }}>
                  <select className="border rounded px-1 py-1" value={r.team} onChange={(e) => setRow(i, { team: e.target.value })}><option value="A">A</option><option value="B">B</option></select></td> : null}
                <td style={{ border: '1px solid #eee', padding: '6px 8px', fontWeight: 700, color: PRIMARY }}>{ch ?? '—'}</td>
                <td style={{ border: '1px solid #eee', padding: '6px 8px', fontWeight: 700, color: PRIMARY }}>{ph ?? '—'}</td>
                <td style={{ border: '1px solid #eee', padding: '6px 8px' }}>{strokes ?? '—'}</td>
                <td style={{ border: '1px solid #eee', padding: '2px 6px' }}><button onClick={() => delRow(i)} style={{ color: '#000', fontWeight: 900, background: '#fff', border: '1px solid #9AA0A6', borderRadius: 6, padding: '3px 8px' }}>✕</button></td>
              </tr>
            )
          })}</tbody>
        </table>
      </div>

      <button onClick={addRow} className="text-sm font-bold mt-2" style={{ color: HUSTLER ? '#9CC400' : PRIMARY }}>+ Add player</button>

      <div className="flex items-center gap-2 pt-4">
        <button className="btn-primary" disabled={busy} style={{ backgroundColor: PRIMARY }} onClick={create}>{busy ? 'Creating…' : 'Create round & share'}</button>
      </div>
    </div>
  )
}

function GroupRoundView({ id }) {
  const [rec, setRec] = useState(undefined)
  useEffect(() => { loadMemberGame(id).then(setRec).catch(() => setRec(null)) }, [id])
  if (rec === undefined) return <div className="card">Loading…</div>
  if (!rec) return <div className="card"><p className="text-gray-600">Round not found (or not shared with you).</p></div>
  const meta = rec.meta || {}
  const o = meta.options || {}
  const players = Array.isArray(rec.players) ? rec.players : []
  const groups = Array.from({ length: Number(o.groups) || 1 }, (_, g) => g + 1)
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1"><span style={{ fontSize: 22 }}>📐</span><h3 className="font-bold text-xl">{meta.name || 'Group Round'}</h3></div>
      <p className="text-sm text-gray-600 mb-4">{meta.course?.name} · Tee {meta.course?.teeName} ({meta.course?.rating}/{meta.course?.slope}) · {String(o.gameType || '').toUpperCase()} · ${o.bet || 0}/hole · Net</p>
      {groups.map((g) => {
        const gp = players.filter((p) => (Number(p.group) || 1) === g)
        if (!gp.length) return null
        return (
          <div key={g} className="mb-4">
            <div className="text-sm font-extrabold uppercase tracking-wide mb-2" style={{ color: PRIMARY }}>Group {g}</div>
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['Player', 'Index', 'CH', 'PH'].map((h) => <th key={h} style={{ border: '1px solid #eee', padding: '6px 8px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
              <tbody>{gp.map((p) => (
                <tr key={p.id}>
                  <td style={{ border: '1px solid #eee', padding: '6px 8px', fontWeight: 700 }}>{p.name}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px 8px' }}>{p.handicapIndex ?? '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px 8px', fontWeight: 700, color: PRIMARY }}>{p.courseHandicap}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px 8px', fontWeight: 700, color: PRIMARY }}>{p.playingHandicap}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      })}
      <p className="text-xs text-gray-500 mt-2">Play this round live on the course in the Golf Games app.</p>
    </div>
  )
}
